import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/ui/PageHeader';
import { DEFAULT_FILTERS, type QueueFilters } from '@/lib/aiPipeline';
import { cn } from '@/lib/utils';

import { GenerateTab } from './aiPipeline/GenerateTab';
import { ItemCard } from './aiPipeline/ItemCard';
import { PromptsTab } from './aiPipeline/PromptsTab';
import { QueueTab } from './aiPipeline/QueueTab';

/**
 * Движок ИИ-заданий (D27, 11.09.2026): генерация → проверка качества → проверка
 * формата и ТЗ → решение человека. Только admin — маршрут закрыт `RequireAdmin`,
 * пункт меню — набором «только admin» в `AppShell`, ручки — `RequireRole("admin")`.
 *
 * Перенос песочницы `docs/refs/ai-pipeline-sandbox/index.html`, проверенной на стенде.
 * Токен — из общего входа: клиент API сам продлевает его, 15 минут песочницы здесь нет.
 *
 * Адрес несёт состояние: `?tab=generate|prompts` (очередь — без параметра) и
 * `?item=<id>` — карточку можно открыть по ссылке, маршрут один.
 */

const TABS = ['queue', 'generate', 'prompts'] as const;
type Tab = (typeof TABS)[number];

export function AdminAiPipelinePage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const itemId = (params.get('item') ?? '').trim();
  const rawTab = params.get('tab') ?? '';
  const tab: Tab = (TABS as readonly string[]).includes(rawTab) ? (rawTab as Tab) : 'queue';
  // Фильтры очереди и последний заказ — на странице: карточка и смена вкладки их не сбрасывают.
  const [filters, setFilters] = useState<QueueFilters>(DEFAULT_FILTERS);
  const [genItemId, setGenItemId] = useState('');

  const go = (next: { tab?: Tab; item?: string }) => {
    const p = new URLSearchParams();
    const nextTab = next.tab ?? tab;
    if (nextTab !== 'queue') p.set('tab', nextTab);
    if (next.item) p.set('item', next.item);
    setParams(p);
  };

  return (
    <>
      <PageHeader title={t('adminAiPipeline.title')} subtitle={t('adminAiPipeline.subtitle')} />

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((k) => {
          const on = !itemId && tab === k;
          return (
            <button
              key={k}
              type="button"
              data-ai-tab={k}
              aria-pressed={on}
              onClick={() => go({ tab: k })}
              className={cn(
                'rounded-full px-4 py-1.5 text-sm font-medium transition',
                on ? 'bg-primary text-on-primary' : 'bg-surface-container text-on-surface-variant',
              )}
            >
              {t(`adminAiPipeline.tabs.${k}`)}
            </button>
          );
        })}
      </div>

      {itemId ? (
        <ItemCard key={itemId} id={itemId} onBack={() => go({})} />
      ) : tab === 'generate' ? (
        <GenerateTab itemId={genItemId} onItem={setGenItemId} onOpen={(id) => go({ item: id })} />
      ) : tab === 'prompts' ? (
        <PromptsTab />
      ) : (
        <QueueTab filters={filters} onFilters={setFilters} onOpen={(id) => go({ item: id })} />
      )}
    </>
  );
}
